import * as React from 'react';
import { DefaultButton, Dropdown, IconButton, IDropdownOption, Stack, Text } from '@fluentui/react';
import { ArtifactSummary, getArtifactTypeLabel } from '../../shared';

interface DefinitionRelationshipsEditorProps {
    title?: string;
    description?: string;
    emptyText?: string;
    addLabel?: string;
    addButtonText?: string;
    emphasizeRelatedArtifacts?: boolean;
    showArtifactTypeLabel?: boolean;
    relatedArtifacts: ArtifactSummary[];
    relationshipOptions: IDropdownOption[];
    relationshipToAddId?: number;
    onRelationshipToAddChange: (artifactId?: number) => void;
    onAddRelationship: () => void;
    onRemoveRelationship: (artifactId: number) => void;
}

export function DefinitionRelationshipsEditor(props: DefinitionRelationshipsEditorProps) {
    const {
        title,
        description,
        emptyText,
        addLabel,
        addButtonText,
        emphasizeRelatedArtifacts,
        showArtifactTypeLabel = true,
        relatedArtifacts,
        relationshipOptions,
        relationshipToAddId,
        onRelationshipToAddChange,
        onAddRelationship,
        onRemoveRelationship,
    } = props;

    return (
        <div style={{ border: '1px solid #e1dfdd', borderRadius: 6, padding: 12 }}>
            <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="mediumPlus">{title ?? 'Artifact relationships'}</Text>
                {description && (
                    <Text styles={{ root: { color: '#605e5c' } }}>
                        {description}
                    </Text>
                )}
                {relatedArtifacts.length === 0 ? (
                    <Text>{emptyText ?? 'No related artifacts.'}</Text>
                ) : (
                    <Stack tokens={{ childrenGap: 8 }}>
                        {relatedArtifacts.map(artifact => (
                            <Stack key={artifact.id} horizontal horizontalAlign="space-between" verticalAlign="center">
                                <Text styles={emphasizeRelatedArtifacts ? { root: { fontWeight: 600 } } : undefined}>
                                    {artifact.name}
                                    {showArtifactTypeLabel ? ` (${getArtifactTypeLabel(artifact.type)})` : ''}
                                    {artifact.disabled ? ' - disabled' : ''}
                                </Text>
                                <IconButton
                                    iconProps={{ iconName: 'Cancel' }}
                                    title="Remove relationship"
                                    ariaLabel="Remove relationship"
                                    onClick={() => onRemoveRelationship(artifact.id)}
                                />
                            </Stack>
                        ))}
                    </Stack>
                )}
                <Stack horizontal wrap tokens={{ childrenGap: 8 }} styles={{ root: { rowGap: 8 } }} verticalAlign="end">
                    <div style={{ minWidth: 280, flex: 1 }}>
                        <Dropdown
                            label={addLabel ?? 'Add artifact'}
                            options={relationshipOptions}
                            selectedKey={relationshipToAddId}
                            onChange={(_, option) => onRelationshipToAddChange(option?.key ? Number(option.key) : undefined)}
                            placeholder={relationshipOptions.length > 0 ? 'Select an artifact' : 'No enabled artifacts available'}
                            disabled={relationshipOptions.length === 0}
                        />
                    </div>
                    <DefaultButton
                        text={addButtonText ?? 'Add relationship'}
                        onClick={onAddRelationship}
                        disabled={!relationshipToAddId}
                    />
                </Stack>
            </Stack>
        </div>
    );
}
